import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxCell, CheckboxHeader, TextCell, TextWithHoverAction } from '@/components/tables/contentFields';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import EmptyTableView from '@/components/tables/EmptyTableView';
import TableLoadingView from '@/components/tables/TableLoadingView';
import Table, { TableHeader, TableBody } from '@/components/tables/TableBasics';
import { FirmwareStateLabel } from './FirmwareStateLabel';
import type { Country, Firmware, IdNameType } from '../../types';
import { formatDate } from '@/utils/dateUtils';

interface FirmwareTableProps {
  firmwares: Firmware[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onFirmwareClick: (firmware: Firmware) => void;
  firmwareTypes: IdNameType[];
  countries: Country[];
}

export function FirmwareTable({
  firmwares,
  isLoading,
  selectedIds,
  onSelectionChange,
  onFirmwareClick,
  firmwareTypes
}: FirmwareTableProps) {
  const { t } = useTranslation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const allSelected =
    firmwares.length > 0 && selectedIds.size === firmwares.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(firmwares.map((f) => f.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  if (isLoading && firmwares.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={4} withHeader={true} />
    );
  }

  if (firmwares.length === 0) {
    return <EmptyTableView />;
  }

  return (
    <div className="relative grow min-h-0">
      {isLoading && firmwares.length > 0 && (
        <div className="absolute top-2 right-2 z-10">
          <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}
      <Table className="min-h-0 h-full">
        <TableHeader>
          <CheckboxHeader
            checked={allSelected}
            onChange={handleSelectAll}
            ariaLabel={t('firmware.table.selectAll')}
            indeterminate={someSelected}
          />

          <NonSortableColumnHeader
            label={t('firmware.table.columns.state')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('firmware.sidePanel.type')}
            minWidth="100px"
          />

          <NonSortableColumnHeader
            label={t('firmware.sidePanel.versionName')}
            minWidth="200px"
          />

          <NonSortableColumnHeader
            label={t('firmware.table.columns.releaseDate')}
            minWidth="150px"
          />
        </TableHeader>
        <TableBody>
          {firmwares.map((firmware) => (
            <tr
              key={firmware.id}
              className="hover:bg-surface-darker/50"
              onMouseEnter={() => setHoveredRowId(firmware.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <CheckboxCell
                checked={selectedIds.has(firmware.id)}
                onChange={() => handleSelectRow(firmware.id)}
                ariaLabel={t('firmware.table.selectRow', { name: firmware.versionString })}
              />

              <td className="size-px whitespace-nowrap px-4 py-1">
                <FirmwareStateLabel state={firmware.state} />
              </td>
              <FirmwareTypeRow firmware={firmware} firmwareTypeItems={firmwareTypes} />
              <NameColumn firmware={firmware} hoveredRowId={hoveredRowId} onFirmwareClick={onFirmwareClick} />

              <TextCell value={formatDate(firmware.releaseDate)} />
            </tr>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function NameColumn({ firmware, hoveredRowId, onFirmwareClick }: { firmware: Firmware, hoveredRowId: string | null, onFirmwareClick: (firmware: Firmware) => void }) {
  const { t } = useTranslation();
  let value = firmware.firmwareName ?? firmware.versionString
  if (value === "") {
    value = firmware.versionString
  }

  return <TextWithHoverAction
    value={value}
    isHovered={hoveredRowId === firmware.id}
    onCellClick={() => onFirmwareClick(firmware)}
    onActionClick={() => onFirmwareClick(firmware)}
    actionLabel={t('firmware.table.open')} />;
}

function FirmwareTypeRow({ firmware, firmwareTypeItems }: { firmware: Firmware, firmwareTypeItems: IdNameType[] }) {
  const resolvedType = useMemo(
    () => firmwareTypeItems.find((item) => item.id === firmware.firmwareTypeID)?.name || firmware.firmwareTypeID.toString(),
    [firmwareTypeItems, firmware.firmwareTypeID]
  );
  return <td className="size-px whitespace-nowrap px-4 py-1">
    {resolvedType}
  </td>;
}

